import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Download,
  FileCode2,
  Image as ImageIcon,
  Printer,
  Trash2,
} from "lucide-react";
import { loadBwip } from "@/lib/bwip";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FaqSection } from "@/components/seo/FaqSection";
import { Badge } from "@/components/ui/badge";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { breadcrumbJsonLd, ldScript, pageHead, softwareAppJsonLd } from "@/lib/seo";

const PATH = "/bulk-barcode-generator";
const TITLE = "Bulk Barcode Generator — UPC, EAN, Code 128 Labels";
const DESC =
  "Free bulk barcode generator. Paste a list of UPC, EAN, Code 128, Code 39 or ITF-14 values and print, download a combined SVG sheet, or save as PNG.";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Generators", path: "/generators" },
  { name: "Bulk Barcode Generator", path: PATH },
];

const FAQ = [
  {
    q: "How Many Barcodes Can Be Generated at Once?",
    a: "Up to 200 per batch. The cap keeps the live preview snappy and the print sheet readable. For larger runs, split the list and re-paste — your settings are preserved between batches.",
  },
  {
    q: "What Input Format Does the Textarea Accept?",
    a: "One value per line. Optionally add a caption after a pipe character, e.g. 5901234123457|Cherry jam 250g. Blank lines are ignored and leading or trailing whitespace is trimmed.",
  },
  {
    q: "Can I Print the Barcodes on Avery-Style Label Sheets?",
    a: "Yes. Use the Print sheet button — the browser print dialog includes 'Save as PDF', and the layout is laid out as a flowable grid so it pages cleanly. Choose your label paper in the printer dialog and use 'Fit to page' for best alignment.",
  },
  {
    q: "What's the Difference Between the Per-Row PNG and the Combined SVG Sheet?",
    a: "Per-row PNG downloads a single barcode at the preview resolution — useful for embedding one label in a document. The combined SVG sheet packages every barcode into a single vector file you can drop into a design tool or print at any size without quality loss.",
  },
  {
    q: "Are the Check Digits Computed for Me?",
    a: "Yes. For UPC-A, EAN-13, EAN-8 and ITF-14, if you paste the base digits without a check digit the generator appends the correct GS1 mod-10 check digit for each line. Lines that already include the check digit are validated and used as-is.",
  },
  {
    q: "Is My Data Uploaded?",
    a: "No. Every barcode is rendered locally with the open-source bwip-js library. The list you paste and the files you download never leave your browser.",
  },
  {
    q: "Is the Bulk Barcode Generator Free?",
    a: "Yes. There is no fee, account or watermark, and because rendering happens in your browser there is no server quota either. The numbers are a separate matter: retail UPC and EAN numbers have to come from a GS1 company prefix licensed to your brand.",
  },
  {
    q: "Can I Add Text Under Each Barcode?",
    a: "Yes. The Show value under each barcode switch, on by default, prints the encoded value as readable text below the bars. A caption added after a pipe appears above the barcode in the on-screen grid and the printout, and below it in the combined SVG sheet.",
  },
];

type FormatId = "upca" | "ean13" | "ean8" | "code128" | "code39" | "itf14" | "rationalizedCodabar";

type FormatDef = {
  id: FormatId;
  label: string;
  placeholderLine: string;
  validate?: (v: string) => string | null;
};

function computeMod10Check(digits: string): string {
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    const d = digits.charCodeAt(digits.length - 1 - i) - 48;
    sum += i % 2 === 0 ? d * 3 : d;
  }
  const mod = sum % 10;
  return String(mod === 0 ? 0 : 10 - mod);
}

const FORMATS: FormatDef[] = [
  {
    id: "ean13",
    label: "EAN-13 (12 or 13 digits)",
    placeholderLine: "5901234123457",
    validate: (v) => {
      if (!/^\d+$/.test(v)) return "EAN-13 only allows digits 0-9.";
      if (v.length !== 12 && v.length !== 13)
        return `EAN-13 requires 12 or 13 digits (got ${v.length}).`;
      if (v.length === 13 && computeMod10Check(v.slice(0, 12)) !== v[12])
        return "EAN-13 check digit is invalid.";
      return null;
    },
  },
  {
    id: "upca",
    label: "UPC-A (11 or 12 digits)",
    placeholderLine: "036000291452",
    validate: (v) => {
      if (!/^\d+$/.test(v)) return "UPC-A only allows digits 0-9.";
      if (v.length !== 11 && v.length !== 12)
        return `UPC-A requires 11 or 12 digits (got ${v.length}).`;
      if (v.length === 12 && computeMod10Check(v.slice(0, 11)) !== v[11])
        return "UPC-A check digit is invalid.";
      return null;
    },
  },
  {
    id: "ean8",
    label: "EAN-8 (7 or 8 digits)",
    placeholderLine: "96385074",
    validate: (v) => {
      if (!/^\d+$/.test(v)) return "EAN-8 only allows digits 0-9.";
      if (v.length !== 7 && v.length !== 8)
        return `EAN-8 requires 7 or 8 digits (got ${v.length}).`;
      if (v.length === 8 && computeMod10Check(v.slice(0, 7)) !== v[7])
        return "EAN-8 check digit is invalid.";
      return null;
    },
  },
  {
    id: "code128",
    label: "Code 128 (alphanumeric)",
    placeholderLine: "ASSET-00001",
    validate: (v) => {
      if (v.length === 0) return "Empty value.";
      if (v.length > 80) return `Code 128 is limited to 80 characters (got ${v.length}).`;
      if (!/^[\x20-\x7E]+$/.test(v))
        return "Code 128 only allows printable ASCII (no tabs / line breaks / non-Latin).";
      return null;
    },
  },
  {
    id: "code39",
    label: "Code 39 (uppercase)",
    placeholderLine: "BOX-001",
    validate: (v) => {
      if (v.length === 0) return "Empty value.";
      if (v.length > 43) return `Code 39 is limited to 43 characters (got ${v.length}).`;
      if (!/^[A-Z0-9 \-.$/+%]+$/.test(v))
        return "Code 39 only allows uppercase A-Z, 0-9 and - . $ / + % SPACE.";
      return null;
    },
  },
  {
    id: "itf14",
    label: "ITF-14 (13 or 14 digits)",
    placeholderLine: "10614141000415",
    validate: (v) => {
      if (!/^\d+$/.test(v)) return "ITF-14 only allows digits 0-9.";
      if (v.length !== 13 && v.length !== 14)
        return `ITF-14 requires 13 or 14 digits (got ${v.length}).`;
      if (v.length === 14 && computeMod10Check(v.slice(0, 13)) !== v[13])
        return "ITF-14 check digit is invalid.";
      return null;
    },
  },
  {
    id: "rationalizedCodabar",
    label: "Codabar",
    placeholderLine: "A1234567B",
    validate: (v) => {
      if (v.length < 3) return "Codabar needs start char, data and stop char.";
      if (v.length > 60) return `Codabar is limited to 60 characters (got ${v.length}).`;
      if (!/^[A-D][0-9\-$:/.+]*[A-D]$/.test(v))
        return "Codabar must start and end with A-D and only contain digits and - $ : / . + in between.";
      return null;
    },
  },
];

function withCheckDigit(format: FormatId, value: string): string {
  switch (format) {
    case "upca":
      return value.length === 11 ? value + computeMod10Check(value) : value;
    case "ean13":
      return value.length === 12 ? value + computeMod10Check(value) : value;
    case "ean8":
      return value.length === 7 ? value + computeMod10Check(value) : value;
    case "itf14":
      return value.length === 13 ? value + computeMod10Check(value) : value;
    default:
      return value;
  }
}

const MAX_ITEMS = 200;

type ParsedItem = {
  raw: string;
  value: string;
  caption: string | null;
  encoded: string;
  error: string | null;
};

function parseInput(format: FormatId, raw: string): ParsedItem[] {
  const def = FORMATS.find((f) => f.id === format)!;
  const lines = raw.split(/\r?\n/);
  const items: ParsedItem[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const pipeIdx = trimmed.indexOf("|");
    const value = (pipeIdx === -1 ? trimmed : trimmed.slice(0, pipeIdx)).trim();
    const caption = pipeIdx === -1 ? null : trimmed.slice(pipeIdx + 1).trim() || null;
    const validationErr = def.validate ? def.validate(value) : null;
    items.push({
      raw: trimmed,
      value,
      caption,
      encoded: validationErr ? value : withCheckDigit(format, value),
      error: validationErr,
    });
    if (items.length >= MAX_ITEMS) break;
  }
  return items;
}

function safeFilename(value: string): string {
  return value.replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 40) || "barcode";
}

function BulkBarcodeGeneratorPage() {
  const [format, setFormat] = useState<FormatId>("ean13");
  const [raw, setRaw] = useState(
    "5901234123457|Cherry jam 250g\n5012345678900|Pasta sauce 400g\n4006381333931|Pencil case\n",
  );
  const [scale, setScale] = useState(2);
  const [height, setHeight] = useState(12);
  const [columns, setColumns] = useState(3);
  const [showText, setShowText] = useState(true);
  const [showCaption, setShowCaption] = useState(true);
  const [copied, setCopied] = useState(false);

  const items = useMemo(() => parseInput(format, raw), [format, raw]);
  const validItems = useMemo(() => items.filter((i) => !i.error), [items]);
  const invalidItems = useMemo(() => items.filter((i) => i.error), [items]);

  const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const [svgMap, setSvgMap] = useState<Map<number, string>>(new Map());

  useEffect(() => {
    let cancelled = false;
    loadBwip().then((bwipjs) => {
      if (cancelled) return;
      const next = new Map<number, string>();
      items.forEach((item, idx) => {
        if (item.error) return;
        const opts = {
          bcid: format,
          text: item.encoded,
          scale,
          height,
          includetext: showText,
          textxalign: "center" as const,
          backgroundcolor: "FFFFFF",
        };
        try {
          const canvas = canvasRefs.current.get(idx);
          if (canvas) bwipjs.toCanvas(canvas, opts);
          next.set(idx, bwipjs.toSVG(opts));
        } catch {
          next.set(idx, "");
        }
      });
      setSvgMap(next);
    });
    return () => {
      cancelled = true;
    };
  }, [items, format, scale, height, showText]);

  const downloadPng = (idx: number) => {
    const c = canvasRefs.current.get(idx);
    if (!c) return;
    c.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeFilename(items[idx].encoded)}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  const downloadSvg = (idx: number) => {
    const svg = svgMap.get(idx);
    if (!svg) return;
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safeFilename(items[idx].encoded)}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCombinedSvg = () => {
    if (validItems.length === 0) return;
    const cols = Math.max(1, columns);
    const tileW = 260;
    const tileH = showCaption ? 130 : 110;
    const rows = Math.ceil(validItems.length / cols);
    const sheetW = tileW * cols;
    const sheetH = tileH * rows;
    const parts: string[] = [
      `<?xml version="1.0" encoding="UTF-8"?>`,
      `<svg xmlns="http://www.w3.org/2000/svg" width="${sheetW}" height="${sheetH}" viewBox="0 0 ${sheetW} ${sheetH}">`,
      `<rect width="${sheetW}" height="${sheetH}" fill="#ffffff" />`,
    ];
    validItems.forEach((item, vi) => {
      const idx = items.indexOf(item);
      const inner = svgMap.get(idx);
      if (!inner) return;
      const col = vi % cols;
      const row = Math.floor(vi / cols);
      const x = col * tileW;
      const y = row * tileH;
      // Keep bwip-js's own `viewBox` and add sizing attributes to the opening
      // tag rather than replacing it. A nested <svg> with width/height but no
      // viewBox renders its content at native user-units (no scaling), so the
      // barcode overflows and overlaps neighbouring cells at higher scales.
      // Preserving viewBox + preserveAspectRatio makes each barcode scale to
      // fit its tile.
      const stripped = inner
        .replace(/^<\?xml[^?]*\?>/, "")
        .replace(
          /<svg\b/,
          `<svg x="0" y="0" width="${tileW - 20}" height="${
            tileH - (showCaption ? 30 : 20)
          }" preserveAspectRatio="xMidYMid meet"`,
        );
      parts.push(`<g transform="translate(${x + 10}, ${y + 10})">${stripped}`);
      if (showCaption && item.caption) {
        parts.push(
          `<text x="${(tileW - 20) / 2}" y="${tileH - 10}" text-anchor="middle" font-family="sans-serif" font-size="11" fill="#111">${escapeXml(
            item.caption,
          )}</text>`,
        );
      }
      parts.push(`</g>`);
    });
    parts.push(`</svg>`);
    const blob = new Blob([parts.join("\n")], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `barcodes-${format}-x${validItems.length}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printSheet = () => {
    if (typeof window !== "undefined") window.print();
  };

  const copyValues = async () => {
    try {
      await navigator.clipboard.writeText(validItems.map((i) => i.encoded).join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  const clearAll = () => setRaw("");

  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="Bulk Barcode Generator"
      description="Paste a list of values, pick a 1D format and generate every barcode in one go. Per-row PNG, a combined SVG sheet, or print straight from the browser. For UPC-A, EAN-13, EAN-8 and ITF-14 lines entered without the last digit, the generator appends the GS1 mod-10 check digit."
      crumbs={CRUMBS}
      hideContentInPrint
      content={
        <>
          <ToolSeoSections
            aboutHeading="About the bulk barcode generator"
            about={
              <>
                <p>
                  The ReadBarcode bulk barcode generator turns a pasted list of GTINs, asset tags or
                  SKUs into a sheet of printable barcodes in a single pass. It supports the seven
                  everyday 1D symbologies — UPC-A, EAN-13, EAN-8, Code 128, Code 39, ITF-14 and
                  Codabar — and renders them locally with the open-source bwip-js library so no data
                  ever leaves your device.
                </p>
                <p>
                  Pair each value with an optional caption (after a pipe character) and the caption is
                  printed above the barcode, ready for inventory bins, library cards, asset tags or
                  small-batch packaging proofs. Download per-row PNGs, a combined SVG sheet for a
                  designer, or send the live grid straight to your browser&apos;s print dialog.
                </p>
              </>
            }
            whenToUse={[
              "Pre-printing inventory labels for a warehouse receiving day.",
              "Generating asset tags (Code 128) for a hardware refresh.",
              "Producing a sheet of EAN-13 proofs before sending a packaging design to print.",
              "Issuing library or membership cards with Codabar.",
              "Printing ITF-14 carton labels in one batch after a catalogue update.",
              "Spot-printing replacement barcodes for damaged retail packaging.",
            ]}
            howItWorks={[
              {
                title: "Pick the symbology",
                desc: "Choose UPC, EAN, Code 128 or another 1D format — every line in the batch uses the same format.",
              },
              {
                title: "Paste your values",
                desc: "One value per line, with an optional |caption suffix. Up to 200 lines per batch.",
              },
              {
                title: "Validate",
                desc: "Each line is checked for length, allowed characters and check digit. Errors are reported inline so you can fix and re-paste.",
              },
              {
                title: "Tune size & layout",
                desc: "Adjust scale, bar height and the number of columns on the sheet.",
              },
              {
                title: "Print or download",
                desc: "Print straight from the browser, save a single combined SVG sheet, or grab per-row PNG / SVG files.",
              },
            ]}
            whatToAvoid={[
              "Mixing formats in one batch — change the dropdown and re-render rather than guessing what symbology each line is.",
              "Printing at less than 80% of the GS1 nominal size for retail UPC / EAN labels.",
              "Stretching the printed sheet to a paper size that's smaller than designed — use 'Actual size' in the print dialog.",
              "Stuffing thousands of values into one batch — split into runs of 200 to keep the live preview responsive.",
              "Using the bulk tool to issue brand-new UPCs you haven't been allocated by GS1.",
            ]}
            tips={[
              "Use captions to encode SKU names so warehouse staff don't need a lookup sheet.",
              "Pick 3 or 4 columns for letter / A4 paper with a 2-3 mm bar height bump for thermal printers.",
              "Always export an SVG sheet first — you can resize it without losing crispness, then rasterise to PNG only if needed.",
              "Round-trip-test one barcode with the Online Barcode Reader before printing the full run.",
              "For Code 128 asset tags, keep the encoded value short and ASCII-only to maximise scanner reliability.",
            ]}
          />
          <PrivacyNote />
          <h2>Bulk Barcode Generator for Code 128</h2>
          <p>To use this as a free bulk barcode generator for Code 128, choose Code 128 in the Format menu: every line is then encoded as Code 128, letters and digits alike.</p>
          <p>Each line takes up to 80 printable ASCII characters, enough for asset tags and SKUs, while tabs and accented letters are flagged as errors. The checksum inside the symbol is added for you, as the <Link to="/barcode-guides/code-128-explained">Code 128 explained guide</Link> shows. The output is plain Code 128, not GS1-128, because no FNC1 character is inserted.</p>
          <h3>Bulk Barcode Generator Online</h3>
          <p>This bulk barcode generator online runs in your browser with no install or account. Each run takes up to 200 lines, and your format and size settings stay in place when you clear the list for the next run.</p>
          <h2>Generate Barcodes in Bulk From an Excel List</h2>
          <p>Copy a column from Excel or Google Sheets and paste it into the Values box; each cell becomes one barcode.</p>
          <ol>
            <li>Format the column as text so leading zeros survive.</li>
            <li>For captions, join value and label in a helper column with <code>=A2&amp;"|"&amp;B2</code>.</li>
            <li>Copy that column, paste it and pick the format.</li>
          </ol>
          <p>Pasting two columns at once fails, because spreadsheets separate them with a tab, not a pipe. Missing UPC, EAN and ITF-14 check digits are appended, and the <Link to="/check-digit-calculator">check digit calculator</Link> shows the math for a single code.</p>
          <h2>Print a Sheet of Barcodes</h2>
          <p>Press Print sheet and the browser prints the barcode grid and captions, without the settings panels or this text.</p>
          <p>The printed grid keeps your Sheet columns setting, from 1 to 5. Print at 100% scale, or choose Save as PDF to keep a file. For one label at a time, use the <Link to="/barcode-generator">online barcode generator</Link>.</p>
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <div className="mb-6 print:hidden"></div>

      <div className="grid gap-6 print:hidden md:grid-cols-2">
        <Card>
          <CardContent className="space-y-5 p-6">
            <div className="space-y-2">
              <Label htmlFor="format">Format</Label>
              <Select value={format} onValueChange={(v) => setFormat(v as FormatId)}>
                <SelectTrigger id="format">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FORMATS.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                One value per line. Optional caption after a pipe:{" "}
                <span className="font-mono">value|caption</span>
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="raw">Values</Label>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>
                    {items.length} / {MAX_ITEMS}
                  </span>
                  {raw && (
                    <button
                      type="button"
                      onClick={clearAll}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-muted"
                    >
                      <Trash2 className="h-3 w-3" aria-hidden /> Clear
                    </button>
                  )}
                </div>
              </div>
              <Textarea
                id="raw"
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                rows={10}
                spellCheck={false}
                autoComplete="off"
                className="font-mono text-xs"
                placeholder={`${FORMATS.find((f) => f.id === format)!.placeholderLine}|Caption (optional)\n...one per line`}
              />
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Badge variant="outline" className="gap-1">
                  <CheckCircle2 className="h-3 w-3 text-primary" aria-hidden />
                  {validItems.length} valid
                </Badge>
                {invalidItems.length > 0 && (
                  <Badge variant="outline" className="gap-1 text-destructive">
                    <AlertCircle className="h-3 w-3" aria-hidden />
                    {invalidItems.length} invalid
                  </Badge>
                )}
                {items.length >= MAX_ITEMS && (
                  <span className="text-muted-foreground">
                    Cap reached — extra lines were dropped.
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-5 p-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Scale</Label>
                <span className="text-xs text-muted-foreground">{scale}x</span>
              </div>
              <Slider
                min={1}
                max={6}
                step={1}
                value={[scale]}
                onValueChange={(v) => setScale(v[0])}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Bar height</Label>
                <span className="text-xs text-muted-foreground">{height} mm</span>
              </div>
              <Slider
                min={5}
                max={30}
                step={1}
                value={[height]}
                onValueChange={(v) => setHeight(v[0])}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Sheet columns</Label>
                <span className="text-xs text-muted-foreground">{columns}</span>
              </div>
              <Slider
                min={1}
                max={5}
                step={1}
                value={[columns]}
                onValueChange={(v) => setColumns(v[0])}
              />
            </div>

            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <Label htmlFor="show-text" className="cursor-pointer">
                Show value under each barcode
              </Label>
              <Switch id="show-text" checked={showText} onCheckedChange={setShowText} />
            </div>

            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <Label htmlFor="show-caption" className="cursor-pointer">
                Show caption above each label
              </Label>
              <Switch id="show-caption" checked={showCaption} onCheckedChange={setShowCaption} />
            </div>

            <div className="grid grid-cols-2 gap-2 pt-2">
              <Button type="button" onClick={printSheet} disabled={validItems.length === 0}>
                <Printer className="h-4 w-4" aria-hidden />
                Print sheet
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={downloadCombinedSvg}
                disabled={validItems.length === 0}
              >
                <FileCode2 className="h-4 w-4" aria-hidden />
                SVG sheet
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={copyValues}
                disabled={validItems.length === 0}
                className="col-span-2"
              >
                <Copy className="h-4 w-4" aria-hidden />
                {copied ? "Copied encoded values" : "Copy encoded values"}
              </Button>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Download className="h-3.5 w-3.5" aria-hidden />
              Files are generated on your device — nothing is uploaded.
            </p>
          </CardContent>
        </Card>
      </div>

      {invalidItems.length > 0 && (
        <section className="mt-6 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm print:hidden">
          <div className="flex items-center gap-2 font-medium text-destructive">
            <AlertCircle className="h-4 w-4" aria-hidden />
            {invalidItems.length} line{invalidItems.length === 1 ? "" : "s"} couldn't be encoded
          </div>
          <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
            {invalidItems.slice(0, 8).map((i, idx) => (
              <li key={idx}>
                <span className="font-mono">{i.value || "(empty)"}</span> — {i.error}
              </li>
            ))}
            {invalidItems.length > 8 && <li>…and {invalidItems.length - 8} more.</li>}
          </ul>
        </section>
      )}

      <section className="mt-8">
        <div
          className="grid gap-4 print:gap-2"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
        >
          {items.map((item, idx) => {
            if (item.error) return null;
            return (
              <div
                key={idx}
                className="flex flex-col items-center gap-2 rounded-md border border-border bg-white p-3 print:border-0 print:p-1"
              >
                {showCaption && item.caption && (
                  <div className="w-full truncate text-center text-xs font-medium text-foreground">
                    {item.caption}
                  </div>
                )}
                <canvas
                  ref={(el) => {
                    if (el) canvasRefs.current.set(idx, el);
                    else canvasRefs.current.delete(idx);
                  }}
                  className="max-h-[160px] max-w-full"
                  aria-label={`Barcode for ${item.encoded}`}
                />
                <div className="flex w-full items-center justify-between gap-2 print:hidden">
                  <code className="truncate text-[10px] text-muted-foreground">{item.encoded}</code>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => downloadPng(idx)}
                      title="Download PNG"
                    >
                      <ImageIcon className="h-3.5 w-3.5" aria-hidden />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => downloadSvg(idx)}
                      title="Download SVG"
                    >
                      <FileCode2 className="h-3.5 w-3.5" aria-hidden />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {validItems.length === 0 && (
          <div className="rounded-md border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            Add at least one valid value above to start previewing barcodes.
          </div>
        )}
      </section>
    </ToolLayout>
  );
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function PrivacyNote() {
  return (
    <>
      <h2>Private by Default</h2>
      <p>
        The batch is rendered locally with the bwip-js library. The values you paste and the files
        you download or print never touch our servers.
      </p>
    </>
  );
}

function RelatedTools() {
  const related = [
    {
      path: "/barcode-generator",
      label: "Online Barcode Generator",
      desc: "Single-barcode version with the same seven 1D formats.",
    },
    {
      path: "/batch-barcode-scanner",
      label: "Batch Barcode Tools",
      desc: "Validate and clean many barcode values from a pasted list.",
    },
    {
      path: "/gtin-validator",
      label: "GTIN Validator",
      desc: "Verify GTIN-8/12/13/14 check digits before printing labels.",
    },
    {
      path: "/check-digit-calculator",
      label: "Check Digit Calculator",
      desc: "Compute the mod-10 check digit for a UPC, EAN or GTIN.",
    },
    {
      path: "/barcode-guides/code-128-explained",
      label: "Code 128 explained",
      desc: "Subsets, FNC1 and what to watch for when decoding it.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-lg font-semibold">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <div className="text-sm font-medium group-hover:text-primary">{r.label}</div>
            <div className="text-xs text-muted-foreground">{r.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}

export const Route = createFileRoute("/bulk-barcode-generator")({
  component: BulkBarcodeGeneratorPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
    });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode Bulk Barcode Generator",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});
